import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

const navLinks = [
  { label: "电影", to: "/category/movie" },
  { label: "电视剧", to: "/category/tvshow" },
  { label: "最新", to: "/#latest" },
  { label: "热门", to: "/#hot" },
  { label: "专题", to: "/#topics" },
  { label: "关于", to: "/about" },
];

export default function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const isHome = location.pathname === "/";
  const transparent = isHome && !scrolled && !menuOpen;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    if (menuOpen) document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [menuOpen]);

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const kw = keyword.trim();
    navigate(kw ? `/search?q=${encodeURIComponent(kw)}` : "/search");
    setMenuOpen(false);
  };

  const textMuted = transparent ? "text-background-100/80 hover:text-background-50" : "text-foreground-600 hover:text-primary-600";
  const textStrong = transparent ? "text-background-50" : "text-foreground-900";

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        transparent
          ? "bg-transparent"
          : "bg-background-50/95 backdrop-blur border-b border-background-200"
      }`}
    >
      <div className="w-full px-4 md:px-8 h-16 flex items-center gap-4">
        <Link to="/" className="flex items-center gap-2 shrink-0">
          <span className="w-9 h-9 rounded-md bg-primary-500 text-background-50 flex items-center justify-center font-heading text-lg font-bold leading-none">
            绍
          </span>
          <span className={`font-heading text-xl font-bold tracking-wide ${textStrong}`}>
            绍宋
            <span className="text-accent-500">.</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1 ml-4">
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={`px-3 py-2 text-sm rounded-md transition-colors whitespace-nowrap ${textMuted}`}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        <form onSubmit={submitSearch} className="hidden md:flex flex-1 max-w-md ml-auto">
          <div className="relative w-full">
            <span className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center ${transparent ? "text-background-100/70" : "text-foreground-400"}`}>
              <i className="ri-search-line text-base" />
            </span>
            <input
              ref={inputRef}
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              type="text"
              placeholder="搜索影片名、导演、演员…"
              aria-label="搜索影视"
              className={`w-full h-10 pl-9 pr-3 text-sm rounded-md border outline-none transition-colors ${
                transparent
                  ? "bg-background-50/15 border-background-50/30 text-background-50 placeholder:text-background-100/60 focus:bg-background-50/25"
                  : "bg-background-100 border-background-200 text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:bg-background-50"
              }`}
            />
          </div>
        </form>

        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          aria-label="打开菜单"
          className={`md:hidden ml-auto w-10 h-10 flex items-center justify-center rounded-md cursor-pointer ${textStrong}`}
        >
          <i className={menuOpen ? "ri-close-line text-2xl" : "ri-menu-line text-2xl"} />
        </button>
      </div>

      {menuOpen && (
        <div ref={panelRef} className="md:hidden bg-background-50 border-t border-background-200 px-4 py-4">
          <form onSubmit={submitSearch} className="relative w-full mb-3">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-foreground-400">
              <i className="ri-search-line text-base" />
            </span>
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              type="text"
              placeholder="搜索影片名、导演、演员…"
              aria-label="搜索影视"
              className="w-full h-10 pl-9 pr-3 text-sm rounded-md border border-background-200 bg-background-100 text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:bg-background-50"
            />
          </form>
          <nav className="grid grid-cols-2 gap-2">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="px-3 py-2.5 text-sm rounded-md bg-background-100 text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors"
              >
                {link.label}
              </Link>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}